<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import WhatsappPersonalAPI from 'dashboard/api/channel/whatsappPersonalChannel';
import QRGenerator from '../components/QRGenerator.vue';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    PageHeader,
    QRGenerator,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      isCreating: false,
      inbox: null,
      channel: null,
      currentStep: 'form', // 'form', 'qr', 'authenticated', 'complete'
      eventListener: null,
      qrCodeImageUrl: '',
      qrExpiryTimer: null,
      sessionStatus: '',
      errorMessage: '',
      phoneNumber: '',
      profileName: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
      accountId: 'getCurrentAccountId',
    }),
  },
  validations: {
    inboxName: { required },
  },
  beforeUnmount() {
    this.stopEventListener();
    this.stopQRExpiryCheck();
  },
  methods: {
    async createInbox() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.isCreating = true;
      this.errorMessage = '';

      try {
        // Create inbox and channel
        const response = await WhatsappPersonalAPI.create({
          inbox: {
            name: this.inboxName,
          },
        });

        this.inbox = response.data;
        this.channel = response.data.channel;
        this.sessionStatus = 'initializing'; // Set initial status
        this.currentStep = 'qr'; // Move to QR step with loading state

        // Start WebSocket event listener only
        this.startEventListener();

        // Safety: if we already have a ready status from a fast webhook, navigate immediately
        if (this.channel?.status === 'ready') {
          router.replace({
            name: 'settings_inboxes_choose_platform',
            params: {
              page: 'new',
              inbox_id: this.inbox.id,
            },
          });
        }
      } catch (error) {
        this.isCreating = false;
        this.errorMessage =
          error.message ||
          this.$t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.ERROR_MESSAGE');
        useAlert(this.errorMessage);
      }
    },

    // Polling methods removed - using WebSocket events only

    // QR code expiry checking - automatically check if QR changed after a timeout
    startQRExpiryCheck() {
      if (this.qrExpiryTimer) {
        clearTimeout(this.qrExpiryTimer);
      }

      // Check for QR expiry after 2 minutes (WhatsApp QR codes typically expire after 20 seconds)
      this.qrExpiryTimer = setTimeout(async () => {
        if (
          this.sessionStatus === 'qr_received' &&
          this.qrCodeImageUrl &&
          this.inbox
        ) {
          try {
            const response = await WhatsappPersonalAPI.getInboxSessionStatus(
              this.inbox.id
            );
            const { qr_code, status } = response.data;

            if (
              status === 'qr_received' &&
              qr_code &&
              qr_code !== this.qrCodeImageUrl
            ) {
              this.qrCodeImageUrl = qr_code;
              this.startQRExpiryCheck(); // Restart the timer for the new QR
            } else if (status !== 'qr_received') {
              // Session status changed, do nothing
            } else {
              // QR might be expired, but still showing - start another check cycle
              this.startQRExpiryCheck();
            }
          } catch (error) {
            // Error checking QR expiry
          }
        }
      }, 120000); // 2 minutes
    },

    stopQRExpiryCheck() {
      if (this.qrExpiryTimer) {
        clearTimeout(this.qrExpiryTimer);
        this.qrExpiryTimer = null;
      }
    },

    async refreshQRCode() {
      if (!this.inbox) return;

      try {
        await WhatsappPersonalAPI.refreshQR(this.inbox.id);
        // Reset to loading state - WebSocket events will handle updates
        this.sessionStatus = 'initializing';
        this.qrCodeImageUrl = '';
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.QR_REFRESH_ERROR')
        );
      }
    },

    restartFlow() {
      this.stopEventListener();
      this.inbox = null;
      this.channel = null;
      this.currentStep = 'form';
      this.isCreating = false;
      this.sessionStatus = '';
      this.qrCodeImageUrl = '';
      this.errorMessage = '';
      this.phoneNumber = '';
      this.profileName = '';
      this.isDoneScanning = false;
    },


    startEventListener() {
      if (!this.channel) {
        return;
      }

      // Listen for WhatsApp authentication events via emitter
      this.eventListener = data => this.handleAuthenticationUpdate(data);
      emitter.on('whatsapp_authentication', this.eventListener);
    },

    stopEventListener() {
      if (this.eventListener) {
        emitter.off('whatsapp_authentication', this.eventListener);
        this.eventListener = null;
      }
    },

    async handleAuthenticationUpdate(data) {
      // Handle WhatsApp authentication status updates for this specific channel
      if (data.channel_id === this.channel?.id) {
        this.sessionStatus = data.status;
        this.phoneNumber = data.phone_number;
        this.profileName = data.profile_name;

        if (data.status === 'qr_received') {
          // QR code is available, update the UI
          this.currentStep = 'qr'; // Keep on QR step, not qr-scan
          this.qrCodeImageUrl = data.qr_code || '';
          // Start QR expiry checking
          this.startQRExpiryCheck();
        } else if (data.status === 'authenticated') {
          // User scanned QR code, show intermediate state
          this.currentStep = 'authenticated';
          this.stopQRExpiryCheck();
        } else if (data.status === 'ready') {
          // WhatsApp session is fully ready
          this.currentStep = 'authenticated';
          this.stopEventListener();
          this.stopQRExpiryCheck();
          this.isCreating = false;

          // Navigate to choose platform step
          router.replace({
            name: 'settings_inboxes_choose_platform',
            params: {
              page: 'new',
              inbox_id: this.inbox.id,
            },
          });
        } else if (
          data.status === 'auth_failure' ||
          data.status === 'disconnected'
        ) {
          this.stopEventListener();
          this.stopQRExpiryCheck();
          this.isCreating = false;
          this.errorMessage = this.$t(
            'INBOX_MGMT.ADD.WHATSAPP_PERSONAL.SESSION_FAILED'
          );
          this.currentStep = 'form';
          useAlert(this.errorMessage);
        }
      } else if (data.inbox_id === this.inbox?.id) {
        // Channel ID might change on restart but inbox ID should match
        this.channel = { ...this.channel, id: data.channel_id };

        // Re-process the event with updated channel
        this.sessionStatus = data.status;
        this.phoneNumber = data.phone_number;
        this.profileName = data.profile_name;

        if (data.status === 'qr_received') {
          this.currentStep = 'qr';
          this.qrCodeImageUrl = data.qr_code || '';
          this.startQRExpiryCheck();
        } else if (data.status === 'authenticated') {
          this.currentStep = 'authenticated';
          this.stopQRExpiryCheck();
        } else if (data.status === 'ready') {
          this.currentStep = 'authenticated';
          this.stopEventListener();
          this.stopQRExpiryCheck();
          this.isCreating = false;

          router.replace({
            name: 'settings_inboxes_choose_platform',
            params: {
              page: 'new',
              inbox_id: this.inbox.id,
            },
          });
        }
      } else {
        // Some systems broadcast to room with multiple channels; ignore others gracefully
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.DESC')"
    />

    <!-- Step 1: Initial Form -->
    <form
      v-if="currentStep === 'form'"
      class="flex flex-wrap mx-0"
      @submit.prevent="createInbox()"
    >
      <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.INBOX_NAME.LABEL') }}
          <input
            v-model.trim="inboxName"
            type="text"
            data-testid="whatsapp_personal_name_input"
            :placeholder="
              $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.INBOX_NAME.PLACEHOLDER')
            "
            @blur="v$.inboxName.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.INBOX_NAME.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full">
        <woot-submit-button
          :loading="isCreating && currentStep === 'form'"
          :disabled="inboxName === ''"
          :button-text="$t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.SUBMIT_BUTTON')"
        />
      </div>
    </form>

    <!-- Step 2: QR Code Section -->
    <div
      v-if="currentStep === 'qr'"
      class="qr-code-section"
      data-testid="whatsapp_personal_qr_step"
      :data-status="sessionStatus"
    >
      <!-- Loading State - show when initializing or waiting for QR -->
      <div
        v-if="
          sessionStatus === 'initializing' ||
          sessionStatus === '' ||
          sessionStatus === 'disconnected'
        "
        class="text-center py-8"
      >
        <div class="spinner mb-4"></div>
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-2">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.INITIALIZING.TITLE') }}
        </h3>
        <p class="text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.INITIALIZING.DESC') }}
        </p>
        <div class="mt-4">
          <woot-button
            color-scheme="alert"
            variant="hollow"
            size="small"
            @click="restartFlow"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.CANCEL') }}
          </woot-button>
        </div>
      </div>

      <!-- QR Code Display -->
      <div
        v-else-if="sessionStatus === 'qr_received' && qrCodeImageUrl"
        class="text-center py-8"
      >
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-4">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.QR_CODE.TITLE') }}
        </h3>

        <div class="qr-code-container mb-6" data-testid="whatsapp_personal_qr">
          <QRGenerator :qrValue="qrCodeImageUrl" />
        </div>

        <div
          class="instructions bg-slate-50 dark:bg-slate-800 p-4 rounded-lg mb-6 text-left max-w-md mx-auto"
        >
          <ol
            class="list-decimal list-inside space-y-2 text-slate-700 dark:text-slate-300"
          >
            <li>{{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.QR_CODE.STEP_1') }}</li>
            <li>{{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.QR_CODE.STEP_2') }}</li>
            <li>{{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.QR_CODE.STEP_3') }}</li>
            <li>{{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.QR_CODE.STEP_4') }}</li>
          </ol>
        </div>

        <div class="flex gap-3 justify-center">
          <woot-button
            color-scheme="secondary"
            variant="hollow"
            size="small"
            @click="refreshQRCode"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.QR_CODE.REFRESH') }}
          </woot-button>

          <woot-button
            color-scheme="alert"
            variant="hollow"
            size="small"
            @click="restartFlow"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.CANCEL') }}
          </woot-button>
        </div>

        <!-- Auto-detection message -->
        <div class="text-center mt-4">
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.QR_AUTO_DETECT') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Step 3: Authentication Success -->
    <div v-if="currentStep === 'authenticated'" class="text-center py-8">
      <div class="success-icon text-green-500 text-6xl mb-4">✅</div>
      <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-2">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.AUTHENTICATED.TITLE') }}
      </h3>
      <p class="text-slate-600 dark:text-slate-400 mb-2">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.AUTHENTICATED.PHONE') }}:
        {{ phoneNumber }}
      </p>
      <p class="text-slate-600 dark:text-slate-400 mb-6">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.AUTHENTICATED.PROFILE') }}:
        {{ profileName }}
      </p>
      <div class="spinner"></div>
      <p class="text-slate-600 dark:text-slate-400 mt-2">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP_PERSONAL.AUTHENTICATED.DESC') }}
      </p>
    </div>

    <!-- Error Message -->
    <div
      v-if="errorMessage && currentStep === 'form'"
      data-testid="whatsapp_personal_error"
      class="error-message mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
    >
      <p class="text-red-600 dark:text-red-400">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<style scoped>
.qr-code-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qr-code-image {
  max-width: 300px;
  height: auto;
}

.instructions ol li {
  margin: 8px 0;
}

.error-message {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
